import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";
import { getClient, updateClient } from "@/services/clientService";
import { setAutoSendEmailOverride } from "@/services/invoiceService";
import { invoiceKeys } from "@/lib/queryKeys";
import {
  PDF_FILENAME_TOKENS,
  previewPdfFilename,
} from "@/lib/pdf-filename-preview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const TOKEN_LABELS_ES: Record<(typeof PDF_FILENAME_TOKENS)[number], string> = {
  tipo: "Tipo de comprobante (101)",
  serie: "Serie (A)",
  numero: "Número (1234)",
  fecha: "Fecha de emisión (AAAA-MM-DD)",
  mes: "Mes en texto (Marzo)",
  cliente: "Nombre del cliente",
  rut: "RUT del cliente",
};

/**
 * Draft-detail content card: how this invoice's PDF and email get sent.
 * Always visible in the page body (not tucked behind an info icon —
 * moved out of a popover per explicit feedback) whenever the receiver
 * client has anything configurable here. Two independent sections,
 * either can render alone:
 *  - Auto-send-email: shown whenever the client wants invoice emails at
 *    all. Edits a per-INVOICE override (not the client), via the narrow
 *    /invoices/:id/auto-send-email patch — mirrors the create-flow override.
 *  - PDF filename template: a "configure this" nudge for unconfigured
 *    clients only (hides once the client has one), edits the CLIENT record.
 */
export function InvoiceSendSettingsCard({
  customerId,
  invoiceId,
  autoSendEmailOverride,
}: {
  customerId: string | undefined;
  invoiceId: string;
  autoSendEmailOverride?: boolean | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [autoSendChoice, setAutoSendChoice] = useState(true);
  const [editingFilename, setEditingFilename] = useState(false);

  const { data: client, isLoading } = useQuery({
    queryKey: ["client", customerId],
    queryFn: () => getClient(customerId!),
    enabled: Boolean(customerId),
  });

  useEffect(() => {
    if (client) setValue(client.pdfFilenameTemplate ?? "");
  }, [client]);

  useEffect(() => {
    if (client) {
      setAutoSendChoice(autoSendEmailOverride ?? client.autoSendInvoiceEmail !== false);
    }
  }, [client, autoSendEmailOverride]);

  const saveFilenameMutation = useMutation({
    mutationFn: () =>
      updateClient(customerId!, {
        pdfFilenameTemplate: value.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", customerId] });
      toast.success(
        t("invoices.detail.pdfNameSaved", "Nombre del PDF actualizado.")
      );
      setEditingFilename(false);
    },
    onError: () => {
      toast.error(
        t("invoices.detail.pdfNameSaveError", "No se pudo guardar el nombre.")
      );
    },
  });

  const saveAutoSendMutation = useMutation({
    mutationFn: (next: boolean) => setAutoSendEmailOverride(invoiceId, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      toast.success(
        t("invoices.detail.autoSendEmailSaved", "Preferencia de envío actualizada.")
      );
    },
    onError: () => {
      toast.error(
        t("invoices.detail.autoSendEmailSaveError", "No se pudo guardar el cambio.")
      );
    },
  });

  if (!customerId || isLoading) return null;

  const showFilenameSection = !client?.pdfFilenameTemplate?.trim();
  const showEmailSection = client?.sendInvoiceByEmail === true;
  if (!showFilenameSection && !showEmailSection) return null;

  const preview = previewPdfFilename(value, {
    language: client?.language === "en" ? "en" : "es",
    cliente: client?.displayName?.trim() || client?.legalName?.trim() || undefined,
    rut: client?.rut?.trim() || undefined,
  });

  function insertToken(token: string): void {
    const el = inputRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}{${token}}${value.slice(end)}`;
    setValue(next);
    requestAnimationFrame(() => {
      const caret = start + token.length + 2;
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t("invoices.detail.sendSettingsTitle", "Envío del comprobante")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {showEmailSection && (
          <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4">
            <Checkbox
              id="detailAutoSendEmailOverride"
              checked={autoSendChoice}
              disabled={saveAutoSendMutation.isPending}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setAutoSendChoice(next);
                saveAutoSendMutation.mutate(next);
              }}
            />
            <div className="grid flex-1 gap-1 leading-none">
              <Label
                htmlFor="detailAutoSendEmailOverride"
                className="cursor-pointer text-sm font-medium leading-none"
              >
                {t("invoices.create.autoSendEmailOverride", "Enviar por email automáticamente al confirmarse")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {autoSendChoice === (client?.autoSendInvoiceEmail !== false)
                  ? t(
                      "invoices.create.autoSendEmailOverrideHelpDefault",
                      "Según la configuración de {{client}}. Podés cambiarlo solo para este comprobante.",
                      { client: client?.displayName ?? client?.legalName }
                    )
                  : t(
                      "invoices.create.autoSendEmailOverrideHelpCustom",
                      "Elegido para este comprobante — no cambia la configuración del cliente."
                    )}
              </p>
            </div>
            {saveAutoSendMutation.isPending && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            )}
          </div>
        )}

        {showFilenameSection && (
          <div className={cn("space-y-3", showEmailSection && "border-t pt-4")}>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <Label htmlFor="detailPdfFilename" className="text-sm font-medium text-foreground">
                  {t("invoices.detail.pdfNameTitle", "Nombre del archivo PDF")}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {preview
                    ? t("clients.form.pdfFilenamePreview", "Vista previa:")
                    : t("clients.form.pdfFilenameCurrentFormat", "Formato actual:")}
                </p>
                <p className="font-mono text-sm font-semibold text-foreground">
                  {preview ?? "CFE-101-A-1234.pdf"}
                </p>
              </div>
              {!editingFilename && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setEditingFilename(true)}
                >
                  {t("invoices.detail.pdfNameChange", "Cambiar")}
                </Button>
              )}
            </div>

            {editingFilename && (
              <div className="space-y-3">
                <Input
                  id="detailPdfFilename"
                  ref={inputRef}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t("clients.form.pdfFilenameInputPlaceholder", "Sin plantilla")}
                  maxLength={255}
                  autoFocus
                />
                <div className="space-y-1">
                  <p className="text-xs font-medium text-foreground">
                    {t("clients.form.pdfFilenameTokensLabel", "Tocá un campo para agregarlo:")}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {PDF_FILENAME_TOKENS.map((tok) => (
                      <button
                        key={tok}
                        type="button"
                        title={TOKEN_LABELS_ES[tok]}
                        onClick={() => insertToken(tok)}
                        className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground transition-colors hover:bg-muted/70"
                      >
                        {`{${tok}}`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setValue(client?.pdfFilenameTemplate ?? "");
                      setEditingFilename(false);
                    }}
                    disabled={saveFilenameMutation.isPending}
                  >
                    {t("invoices.schedule.cancel", "Cancelar")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => saveFilenameMutation.mutate()}
                    disabled={saveFilenameMutation.isPending}
                  >
                    {saveFilenameMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("clients.form.save", "Guardar")
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
